/**
 * actualCost2Parse.js — CHA-323 phase 3. Pure parsers for the C-Air broker exports, extracted so
 * they're unit-testable without a real workbook. Each takes an array-of-arrays (AOA — what
 * `XLSX.utils.sheet_to_json(sheet, { header: 1 })` returns) and returns structured results.
 *
 * These mirror the parse logic validated live 2026-07-16 against the real files.
 */

// Doc-id slug for lookup_broker_invoice (matches functions/scripts/seed-broker-invoice-links.js).
export function slugInvoice(inv) {
  return String(inv || '').replace(/[^A-Za-z0-9_-]/g, '_').toUpperCase();
}

// Invoice-line uploads must resolve into the same lookup keyspace the backend reads.
// Amendment suffixes are removed by normalizeJob before this check.
export function isBrokerJobNumber(job) {
  return /^B?\d+$/.test(String(job || '').trim().toUpperCase());
}

function findHeaderRow(aoa, required) {
  for (let i = 0; i < aoa.length; i++) {
    const cells = (aoa[i] || []).map((c) => (c == null ? '' : String(c).trim()));
    if (required.every((want) => cells.includes(want))) {
      return { index: i, cols: cells };
    }
  }
  return null;
}

/**
 * Invoice-line report: `Job Number` (B-series) + `Invoice Number` (commercial invoice).
 * Returns records (invoice→job, one job per invoice), conflicts (invoice→>1 job), slug collisions.
 */
export function parseInvoiceJob(aoa) {
  const hdr = findHeaderRow(aoa, ['Job Number', 'Invoice Number']);
  if (!hdr) throw new Error("Could not find the 'Job Number' / 'Invoice Number' header row — is this the Invoice Line report?");
  const jc = hdr.cols.indexOf('Job Number');
  const ic = hdr.cols.indexOf('Invoice Number');
  const invToJobs = new Map(); // invoice -> Set(jobs)
  const invalidJobs = [];
  let dataRows = 0;
  for (let i = hdr.index + 1; i < aoa.length; i++) {
    const r = aoa[i] || [];
    // Normalize the job# to the shared keyspace (upper + strip /A suffix) so the lookup matches the
    // ACE/freight keys downstream (self-review #2).
    const job = r[jc] == null ? '' : normalizeJob(r[jc]);
    const inv = r[ic] == null ? '' : String(r[ic]).trim();
    if (!job || !inv) continue;
    dataRows++;
    if (!isBrokerJobNumber(job)) {
      invalidJobs.push({ row: i + 1, commercial_invoice: inv, job_number: job });
      continue;
    }
    if (!invToJobs.has(inv)) invToJobs.set(inv, new Set());
    invToJobs.get(inv).add(job);
  }
  const records = [];
  const conflicts = [];
  const collisions = [];
  const seenSlug = new Map();
  for (const inv of Array.from(invToJobs.keys()).sort()) {
    const jobs = Array.from(invToJobs.get(inv)).sort();
    if (jobs.length > 1) { conflicts.push({ commercial_invoice: inv, jobs }); continue; }
    const key = slugInvoice(inv);
    if (seenSlug.has(key)) { collisions.push({ commercial_invoice: inv, collidesWith: seenSlug.get(key), key }); continue; }
    seenSlug.set(key, inv);
    records.push({ key, commercial_invoice: inv, job_number: jobs[0] });
  }
  return {
    records,
    conflicts,
    collisions,
    invalidJobs,
    stats: { dataRows, distinctInvoices: invToJobs.size, distinctJobs: new Set([].concat(...Array.from(invToJobs.values()).map((s) => Array.from(s)))).size },
  };
}

function toNum(x) {
  if (x == null || x === '') return 0;
  const n = parseFloat(String(x).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

// Normalize a forwarding "Job Invoice #" to the bare B-series job# (strip an /A amendment suffix).
export function normalizeJob(x) {
  return String(x || '').trim().toUpperCase().split(/[/ ]/)[0];
}

/**
 * Forwarding-invoices report: `Job Invoice #` (job#) + `OceanFreight Charges` + `Air Freight Charges`
 * (+ Container#). Sums freight per job (negatives = credits, netted).
 */
export function parseForwarding(aoa) {
  const hdr = findHeaderRow(aoa, ['Job Invoice #', 'OceanFreight Charges', 'Air Freight Charges']);
  if (!hdr) throw new Error("Could not find the forwarding-invoice header ('Job Invoice #' / freight columns) — is this the Forwarding Invoices report?");
  const jc = hdr.cols.indexOf('Job Invoice #');
  const oc = hdr.cols.indexOf('OceanFreight Charges');
  const ac = hdr.cols.indexOf('Air Freight Charges');
  const cc = hdr.cols.indexOf('Container#');
  const byJob = new Map();
  let dataRows = 0;
  // CHA-420 — a non-B-series row used to `continue` silently HERE, one layer above the server, so the
  // server's named rejection never fired for it and the operator saw nothing at all. That is exactly how
  // 1479123 / 1480295 ($21,099 of freight) stayed invisible. Dropping is still correct — a C-Air upload
  // may only carry C-Air jobs — but the drop must be REPORTED.
  //
  // Report everything EXCEPT known report furniture. The default has to be "report it": a missed refusal
  // is the failure this ticket exists to remove, while a spurious line is merely noise. An earlier cut
  // inverted that — it required a >=6-digit run and tested the NORMALIZED token, so `HBL 1234567` (which
  // normalizeJob truncates to `HBL` at the first space) and short refs like `12345` silently vanished all
  // over again. Test the RAW cell, and keep the exclusions explicit and readable rather than emergent.
  const FURNITURE = /^(grand\s*total|sub\s*total|totals?\b|page\b|job\s*invoice|report\s*total|continued)/i;
  const skippedRows = [];
  const skippedSeen = new Set();
  let skipped = 0;
  for (let i = hdr.index + 1; i < aoa.length; i++) {
    const r = aoa[i] || [];
    const raw = String(r[jc] == null ? '' : r[jc]).trim();
    const job = normalizeJob(r[jc]);
    if (!/^B\d+$/.test(job)) { // match the backend validateFreightRows contract (B-series)
      // Count DISTINCT references, not rows. A forwarding file carries several charge lines per shipment
      // (that is why this function aggregates and emits source_rows), so a row-grain count would headline
      // "Not recorded (4)" with the same job listed four times — and the server side, which the UI adds to
      // this, is job-grain because the client pre-aggregates.
      const key = raw.toUpperCase();
      // A row with real charges but a BLANK job cell is the one case the `raw` guard would drop as
      // furniture — and it never reaches the server either, so the server's own '(blank)' rejection can
      // never fire for it. That is freight going nowhere with nothing said about it, which is the exact
      // failure this ticket exists to end (Codex, PR #320).
      if (!raw && (toNum(r[oc]) !== 0 || toNum(r[ac]) !== 0)) {
        skipped++;
        if (skippedRows.length < 50) {
          skippedRows.push({
            job_number: '(blank)',
            reason: 'this row has freight charges but no job number — it cannot be recorded against any shipment',
          });
        }
        continue;
      }
      if (raw && !FURNITURE.test(raw) && !skippedSeen.has(key)) {
        skippedSeen.add(key);
        skipped++; // truthful total; the LIST below is capped and the UI states the gap
        if (skippedRows.length < 50) {
          skippedRows.push({
            job_number: raw,
            reason: 'not a C-Air B-series job — this shipment cannot receive freight from a C-Air file; record it separately',
          });
        }
      }
      continue;
    }
    dataRows++;
    if (!byJob.has(job)) byJob.set(job, { job_number: job, ocean_freight: 0, air_freight: 0, containers: new Set(), rows: 0 });
    const e = byJob.get(job);
    e.ocean_freight += toNum(r[oc]);
    e.air_freight += toNum(r[ac]);
    e.rows++;
    if (cc >= 0 && r[cc]) e.containers.add(String(r[cc]).split(',')[0].trim());
  }
  const rows = Array.from(byJob.values()).map((e) => ({
    job_number: e.job_number,
    ocean_freight: Math.round(e.ocean_freight * 100) / 100,
    air_freight: Math.round(e.air_freight * 100) / 100,
    container: Array.from(e.containers).join(', '),
    source_rows: e.rows,
  }));
  const totalOcean = rows.reduce((s, r) => s + r.ocean_freight, 0);
  const totalAir = rows.reduce((s, r) => s + r.air_freight, 0);
  return { rows, skippedRows, skipped, stats: { dataRows, distinctJobs: rows.length, totalOcean: Math.round(totalOcean), totalAir: Math.round(totalAir) } };
}

// ─────────────────── CHA-370 — broker report-type registry ───────────────────
// C-Air's portal changed its exports (the Invoice Line report gave way to the Declarations
// report), and more formats may come ("I might have another type later" — operator 2026-07-22).
// One registry entry per report type: a header signature to detect it + its parser. detectBrokerReport
// sniffs the AOA and returns the matching entry — the Uploads card routes on `type`.

/**
 * Declarations report ("US Import Declarations Report"): entry grain, NO invoice column — its
 * linking value is job → container(s) for the container bridge. Air rows carry no containers
 * (master bills are air waybills) and yield no pairs; ocean rows do.
 */
export function parseDeclarations(aoa) {
  const hdr = findHeaderRow(aoa, ['Job Number', 'Entry No.', 'Containers']);
  if (!hdr) throw new Error("Could not find the Declarations header ('Job Number' / 'Entry No.' / 'Containers').");
  const jc = hdr.cols.indexOf('Job Number');
  const cc = hdr.cols.indexOf('Containers');
  const ec = hdr.cols.indexOf('Entry No.');
  const fc = hdr.cols.indexOf('First Sale');
  const byJob = new Map();
  let dataRows = 0;
  for (let i = hdr.index + 1; i < aoa.length; i++) {
    const r = aoa[i] || [];
    const job = normalizeJob(r[jc]);
    if (!/^B\d+$/.test(job)) continue;
    dataRows++;
    if (!byJob.has(job)) byJob.set(job, { job_number: job, entry_no: '', first_sale: false, containers: new Set() });
    const e = byJob.get(job);
    if (ec >= 0 && r[ec]) e.entry_no = String(r[ec]).trim();
    if (fc >= 0 && /^y(es)?$/i.test(String(r[fc] || '').trim())) e.first_sale = true; // 'N'/'0' must NOT read true
    for (const raw of String(r[cc] || '').split(',')) {
      const tok = raw.trim().split(/\s/)[0].toUpperCase();
      if (/^[A-Z]{4}\d{7}$/.test(tok)) e.containers.add(tok); // strict ISO 6346 shape
    }
  }
  const rows = Array.from(byJob.values()).map((e) => ({ ...e, containers: Array.from(e.containers).sort() }));
  const containerPairs = rows.reduce((s, r) => s + r.containers.length, 0);
  return { rows, stats: { dataRows, distinctJobs: rows.length, containerPairs, jobsWithContainers: rows.filter((r) => r.containers.length).length } };
}

// Detection order matters: first signature that matches wins (invoice-line is the most specific).
export const BROKER_REPORT_TYPES = [
  { type: 'invoice-line', label: 'US Import Invoice Line Report', required: ['Job Number', 'Invoice Number'], parse: parseInvoiceJob },
  { type: 'declarations', label: 'US Import Declarations Report', required: ['Job Number', 'Entry No.', 'Containers'], parse: parseDeclarations },
  // Future report types: add { type, label, required: [header signature], parse } here.
];

export function detectBrokerReport(aoa) {
  for (const t of BROKER_REPORT_TYPES) {
    if (findHeaderRow(aoa, t.required)) return t;
  }
  return null;
}
